import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ThemeToggle from './ThemeToggle';
import Logo from './Logo';

const { FiMenu, FiX, FiChevronDown } = FiIcons;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [hoverDropdown, setHoverDropdown] = useState(null);
  const location = useLocation();

  const menuItems = [
    { name: 'Inicio', path: '/' },
    { 
      // AJUSTE SOLICITADO: "Intro" en lugar de "Conceptualización"
      name: 'Intro', 
      dropdown: [
        { name: 'Plagio', path: '/conceptualizacion/plagio' },
        { name: 'Propiedad intelectual', path: '/conceptualizacion/propiedad-intelectual' },
        // AJUSTE SOLICITADO: Nuevo submenú "Casos PlaPi"
        { name: 'Casos PlaPi', path: '/intro/casos-plapi' }
      ] 
    },
    { 
      name: 'Juegos', 
      dropdown: [
        { name: 'Plagio', path: '/juegos/plagio' },
        { name: 'Propiedad intelectual', path: '/juegos/propiedad-intelectual' }
      ] 
    },
    { 
      name: 'Resumen', 
      dropdown: [
        { name: 'Plagio', path: '/resumen/plagio' },
        { name: 'Propiedad intelectual', path: '/resumen/propiedad-intelectual' }
      ] 
    },
    { name: 'Pregúntale a PlaPi', path: '/preguntale-a-plapi' },
    { name: 'Referencias', path: '/referencias' }
  ];

  const toggleDropdown = (itemName) => {
    setOpenDropdown(openDropdown === itemName ? null : itemName);
  };

  const handleMouseEnter = (itemName) => {
    if (window.innerWidth >= 1024) setHoverDropdown(itemName);
  };

  const handleMouseLeave = () => {
    if (window.innerWidth >= 1024) setHoverDropdown(null);
  };

  return (
    <motion.header 
      initial={{ y: -50 }} 
      animate={{ y: 0 }} 
      className="fixed top-0 left-0 right-0 z-50 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-b border-gray-200 dark:border-gray-700"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-3">
            <Logo />
            <motion.span className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              PlaPi
            </motion.span>
          </Link>

          <nav className="hidden lg:flex items-center space-x-6">
            {menuItems.map((item) => (
              <div key={item.name} className="relative">
                {item.dropdown ? (
                  <div 
                    className="relative" 
                    onMouseEnter={() => handleMouseEnter(item.name)} 
                    onMouseLeave={handleMouseLeave}
                  >
                    <button 
                      onClick={() => toggleDropdown(item.name)}
                      className="flex items-center space-x-2 text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium py-2 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <span>{item.name}</span>
                      <motion.div animate={{ rotate: (openDropdown === item.name || hoverDropdown === item.name) ? 180 : 0 }}>
                        <SafeIcon icon={FiChevronDown} className="w-4 h-4" />
                      </motion.div>
                    </button>
                    <AnimatePresence>
                      {(openDropdown === item.name || hoverDropdown === item.name) && (
                        <motion.div 
                          initial={{ opacity: 0, y: -10 }} 
                          animate={{ opacity: 1, y: 0 }} 
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute top-full left-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-2"
                        >
                          {item.dropdown.map((subItem) => (
                            <Link 
                              key={subItem.path} 
                              to={subItem.path}
                              onClick={() => { setOpenDropdown(null); setHoverDropdown(null); }}
                              className="flex items-center px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/50 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium transition-colors"
                            >
                              <div className="w-2 h-2 bg-indigo-400 rounded-full mr-3 opacity-60"></div>
                              {subItem.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link 
                    to={item.path}
                    className={`text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium py-2 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 ${location.pathname === item.path ? 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30' : ''}`}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
            <ThemeToggle />
          </nav>

          <div className="lg:hidden flex items-center space-x-4">
            <ThemeToggle />
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-2 text-gray-700 dark:text-gray-300">
              <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="lg:hidden bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 py-4 px-4 space-y-2">
            {menuItems.map((item) => (
              <div key={item.name}>
                {item.dropdown ? (
                  <>
                    <button onClick={() => toggleDropdown(item.name)} className="flex items-center justify-between w-full py-3 px-4 text-gray-700 dark:text-gray-300 font-medium">
                      <span>{item.name}</span>
                      <SafeIcon icon={FiChevronDown} className={`w-4 h-4 transition-transform ${openDropdown === item.name ? 'rotate-180' : ''}`} />
                    </button>
                    {openDropdown === item.name && (
                      <div className="ml-6 space-y-1">
                        {item.dropdown.map((subItem) => (
                          <Link key={subItem.path} to={subItem.path} onClick={() => setIsMenuOpen(false)} className="block py-2 px-4 text-gray-600 dark:text-gray-400 font-medium">
                            {subItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link to={item.path} onClick={() => setIsMenuOpen(false)} className="block py-3 px-4 text-gray-700 dark:text-gray-300 font-medium">
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;